import type { FormatId } from '../parsers/types';

export interface FormatPrimer {
  name: string;
  year: string;
  /** 2–3 short sentences on what the format is and why it exists. */
  about: string;
  /** The shape of a typical file, first to last. */
  layout: string[];
}

export const FORMATS: Record<FormatId, FormatPrimer> = {
  mp3: {
    name: 'MP3',
    year: '1993',
    about:
      'MP3 stores sound as a chain of small, independent **frames**. Each frame holds about 26 ms of audio and starts with its own 4-byte header. Because of this, a player can start anywhere, and a damaged frame only causes a short glitch.',
    layout: ['ID3v2 tag (title, artist, cover art)', 'Info frame (Xing/LAME: length and seek table)', 'Thousands of audio frames', 'Optional ID3v1 tag (last 128 bytes)'],
  },
  mp2: {
    name: 'MPEG Audio Layer II',
    year: '1991',
    about:
      'Layer II is the older sibling of MP3. It is simpler and less efficient, but still common in digital radio and TV. Like MP3, it is a chain of self-contained frames, each with a 4-byte header.',
    layout: ['Optional ID3v2 tag', 'Audio frames of 1,152 samples each', 'Optional ID3v1 tag'],
  },
  aac: {
    name: 'AAC (ADTS)',
    year: '1997',
    about:
      'This is raw AAC audio with no MP4 wrapper. Each frame has a small **ADTS** header that repeats the sample rate and channel count, so a radio stream can be joined at any point.',
    layout: ['Optional ID3v2 tag', 'ADTS frames: 7-byte header + 1,024 samples of AAC data'],
  },
  wav: {
    name: 'WAV',
    year: '1991',
    about:
      'WAV usually holds **uncompressed PCM**: a plain list of sample values, one after another. It is simple and exact, but large. WAV uses Microsoft\'s RIFF layout: a list of labelled **chunks**, each with a 4-letter ID and a size. A program can skip any chunk it does not understand.',
    layout: ['RIFF header ("RIFF", size, "WAVE")', 'fmt chunk: sample rate, channels, bit depth', 'Optional chunks: tags, cue points, broadcast info', 'data chunk: the samples'],
  },
  aiff: {
    name: 'AIFF',
    year: '1988',
    about:
      'AIFF is Apple\'s version of WAV. It also holds uncompressed PCM in labelled chunks. The numbers are **big-endian**, because the first Macs used Motorola processors.',
    layout: ['FORM header ("FORM", size, "AIFF")', 'COMM chunk: channels, frames, bit depth, sample rate', 'SSND chunk: the samples'],
  },
  flac: {
    name: 'FLAC',
    year: '2001',
    about:
      'FLAC is **lossless**: it shrinks audio to about half its size, and you get back every original bit. It **predicts** each sample from the previous ones, then stores only the small error, packed with Rice codes.',
    layout: ['"fLaC" marker', 'STREAMINFO block (always first)', 'Other metadata blocks: seek table, tags, pictures, padding', 'Audio frames, each with a header, one subframe per channel, and a CRC'],
  },
  ogg: {
    name: 'Ogg',
    year: '1993–2002',
    about:
      'Ogg is a **container** that cuts one or more streams into **pages** of up to about 64 KB. Each page has a checksum and a timestamp called the **granule position**. The codec inside is usually Vorbis or Opus.',
    layout: ['Page 1: codec identification header', 'Next pages: comment header (tags) and setup header', 'Audio pages, each carrying one or more packets'],
  },
  mp4: {
    name: 'MPEG-4 (M4A)',
    year: '2001',
    about:
      'An M4A file is a tree of **boxes** (also called atoms). The audio sits in one big `mdat` box. A separate `moov` box holds the tables that say where each audio packet starts and how long it plays. A player needs `moov` before it can play anything.',
    layout: ['ftyp: file type', 'moov: track info, sample tables, tags', 'mdat: the audio packets', '(some files put moov after mdat)'],
  },
};
